import { t } from "@lingui/macro";
import {
  createContext,
  memo,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSignMessage } from "wagmi";

import type { AuthSessionResponse, VerifyResponse } from "lib/apis/auth";
import {
  AUTH_TOKEN_CHANGED_EVENT,
  clearAccessToken,
  getAccessToken,
  getAuthAddress,
  isTokenBoundToAddress,
  loadSession,
  loginWithSiwe,
  saveAccessToken,
} from "lib/auth";
import { helperToast } from "lib/helperToast";
import { isHomeSite } from "lib/legacy";
import useWallet from "lib/wallets/useWallet";

type SignInOptions = {
  address?: string;
  chainId?: number;
};

type AuthContextType = {
  isAuthenticated: boolean;
  isAuthLoading: boolean;
  isSigningIn: boolean;
  isSignInModalOpen: boolean;
  setSignInModalOpen: (open: boolean) => void;
  session: AuthSessionResponse | null;
  authError: string | null;
  signIn: (options?: SignInOptions) => Promise<VerifyResponse | null>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = memo(function AuthProvider({ children }: PropsWithChildren) {
  const { account, active, chainId } = useWallet();
  const { signMessageAsync } = useSignMessage();
  const isHome = isHomeSite();

  const [session, setSession] = useState<AuthSessionResponse | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(() => Boolean(getAccessToken()));
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isSignInModalOpen, setSignInModalOpen] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [tokenEpoch, setTokenEpoch] = useState(0);

  const previousAccountRef = useRef<string | undefined>();
  const signInInFlightRef = useRef(false);
  /** Addresses that already got the sign-in modal this session (avoid reopen spam). */
  const promptedAddressesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const onTokenChanged = () => {
      setTokenEpoch((n) => n + 1);
      if (!getAccessToken()) {
        setSession(null);
      }
    };
    window.addEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);
    return () => window.removeEventListener(AUTH_TOKEN_CHANGED_EVENT, onTokenChanged);
  }, []);

  const signOut = useCallback(() => {
    clearAccessToken();
    setSession(null);
    setAuthError(null);
    setSignInModalOpen(false);
    if (account) {
      promptedAddressesRef.current.delete(account.toLowerCase());
    }
  }, [account]);

  const signIn = useCallback(
    async (options?: SignInOptions) => {
      const address = options?.address ?? account;
      const resolvedChainId = options?.chainId ?? chainId;

      if (!address || !resolvedChainId) {
        setAuthError(t`Connect your wallet to sign in`);
        setSignInModalOpen(true);
        return null;
      }

      if (signInInFlightRef.current) {
        return null;
      }

      signInInFlightRef.current = true;
      setIsSigningIn(true);
      setAuthError(null);
      setSignInModalOpen(true);

      try {
        const result = await loginWithSiwe({
          address,
          chainId: resolvedChainId,
          signMessageAsync,
        });
        setSession({
          address: result.address,
          chainId: result.chainId,
          trader: result.trader,
        });
        promptedAddressesRef.current.add(address.toLowerCase());
        setSignInModalOpen(false);
        helperToast.success(t`Signed in`);
        return result;
      } catch (error) {
        const message = error instanceof Error ? error.message : t`Sign-in failed`;
        setAuthError(message);
        // Keep modal open with the error — do not toast-spam or auto-retry.
        return null;
      } finally {
        signInInFlightRef.current = false;
        setIsSigningIn(false);
      }
    },
    [account, chainId, signMessageAsync]
  );

  // Load / validate session on mount and when wallet account changes.
  useEffect(() => {
    const previousAccount = previousAccountRef.current;
    const nextAccount = account?.toLowerCase();

    if (previousAccount && nextAccount && previousAccount !== nextAccount) {
      clearAccessToken();
      setSession(null);
      setAuthError(null);
      setSignInModalOpen(false);
      promptedAddressesRef.current.delete(previousAccount);
    }

    if (!active || !account) {
      // True disconnect (we had a connected account). Skip initial hydrate before wagmi reconnects.
      if (previousAccount) {
        clearAccessToken();
        promptedAddressesRef.current.clear();
        setAuthError(null);
      }
      previousAccountRef.current = undefined;
      setSession(null);
      setIsAuthLoading(false);
      setSignInModalOpen(false);
      return;
    }

    previousAccountRef.current = nextAccount;

    if (getAccessToken() && !isTokenBoundToAddress(account)) {
      clearAccessToken();
      setSession(null);
      setIsAuthLoading(false);
      return;
    }

    if (!getAccessToken()) {
      setSession(null);
      setIsAuthLoading(false);
      return;
    }

    let cancelled = false;
    setIsAuthLoading(true);

    loadSession()
      .then((loaded) => {
        if (cancelled) return;
        if (loaded && loaded.address.toLowerCase() === account.toLowerCase()) {
          setSession(loaded);
          promptedAddressesRef.current.add(account.toLowerCase());
          if (!getAuthAddress()) {
            const token = getAccessToken();
            if (token) {
              saveAccessToken(token, loaded.address);
            }
          }
        } else {
          clearAccessToken();
          setSession(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsAuthLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [account, active, tokenEpoch]);

  // Connected but not authenticated → show sign-in modal once (no auto API / nonce calls).
  useEffect(() => {
    if (isHome || !active || !account || !chainId || isAuthLoading) {
      return;
    }
    if (session) {
      setSignInModalOpen(false);
      return;
    }
    if (getAccessToken() && isTokenBoundToAddress(account)) {
      return;
    }

    const key = account.toLowerCase();
    if (promptedAddressesRef.current.has(key)) {
      return;
    }

    promptedAddressesRef.current.add(key);
    setAuthError(null);
    setSignInModalOpen(true);
  }, [isHome, active, account, chainId, isAuthLoading, session]);

  const value = useMemo<AuthContextType>(
    () => ({
      isAuthenticated: Boolean(session && account && session.address.toLowerCase() === account.toLowerCase()),
      isAuthLoading,
      isSigningIn,
      isSignInModalOpen,
      setSignInModalOpen,
      session,
      authError,
      signIn,
      signOut,
    }),
    [session, account, isAuthLoading, isSigningIn, isSignInModalOpen, authError, signIn, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
